import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import { authorityApi } from '@/domains/system-management/apis/authority-management';
import type {
  ProgramGroupRequest,
  ProgramParams,
} from '@/domains/system-management/types/authority';
import NoticeMessage from '@/plugin/noticemessage/noticemessage';

export const useAuthorityManagement = (params: ProgramParams) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  // Get program authenticated list
  const useProgramAuthenticated = () => {
    return useQuery({
      queryKey: ['programAuthenticated', params],
      queryFn: () => authorityApi.getProgramAuthenticated(params),
    });
  };

  // Get upper program list
  const useUpperProgram = () => {
    return useQuery({
      queryKey: ['upperProgram'],
      queryFn: () => authorityApi.getUpperProgram(),
    });
  };

  // Get program group
  const useProgramGroup = (id: number) => {
    return useQuery({
      queryKey: ['programGroup', id],
      queryFn: () => authorityApi.getProgramGroup(id),
      enabled: !!id,
    });
  };

  // Create group authority
  const useCreateGroupAuthority = (onCreateSuccess?: () => void) => {
    return useMutation({
      mutationFn: ({ id, data }: { id: number; data: ProgramGroupRequest }) =>
        authorityApi.createGroupAuthority(id, data),
      onSuccess: (_data, id) => {
        // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
        new NoticeMessage(t('msg > registration success'), {
          callback() {
            queryClient.invalidateQueries({ queryKey: ['programGroup', id] });
            queryClient.invalidateQueries({ queryKey: ['navbarList'] });
            if (onCreateSuccess) onCreateSuccess();
          },
        });
      },
      onError: (error: unknown) => {
        const axiosError = error as AxiosError<{ message: string }>;
        const message = axiosError.response?.data?.message || axiosError.message;
        // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
        new NoticeMessage(t(message));
      },
    });
  };

  return {
    useProgramAuthenticated,
    useUpperProgram,
    useProgramGroup,
    useCreateGroupAuthority,
  };
};
